// Deps
import { featuredImage, getImageAspect } from "~/modules/images";
import { createComponent } from "react-fela";
import { combineRules } from "fela";
import { fluid } from "~/styleguide/utils";
import { textStyles } from "~/styleguide/typography";
import get from "lodash.get";

// Components
import SidebarColumn from "~/components/layout/sidebar-column";
import Visual from "~/components/shared/visual";
import RelatedPosts from "~/components/resource/related";
import PostContent from "~/components/shared/post-content";
import TaxonomyList from "~/components/shared/taxonomy/list";
import Video from "~/components/shared/video";
import Blocks from "~/components/blocks";
import HubSpotForm from "~/components/shared/hubspot/form";
import { FelaComponent } from "react-fela";
import readTime from "~/modules/read-time";
import WordpressLink from "~/components/global/links";
// Context
import { useContext } from "react";
import { ResourceContext } from "~/context/content";

// Add an ID to headings on the content
function addHeadingIds(html) {
    if (!html) return html;

    // Regex pattern to match all heading tags (h1, h2, h3, h4, h5, h6)
    const headingRegex = /<(h[1-6])>(.*?)<\/h[1-6]>/g;

    return html.replace(headingRegex, (match, tag, content) => {
        // Remove all HTML tags except for the text inside
        const textOnly = content.replace(/<\/?[^>]+(>|$)/g, "");

        // Remove any leading numeric characters and optional whitespace
        const textWithoutLeadingNumbers = textOnly.replace(/^\d+\s*/, "");

        const id = textWithoutLeadingNumbers
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "");

        return `<${tag} id="${id}">${content}</${tag}>`;
    });
}

export default function ResourceDetail({ resource }) {
    if (!resource) return null;

    const type = useContext(ResourceContext);

    const image = featuredImage(resource, true);

    const acf = get(resource, "acf");
    const modules = get(acf, "modules");
    const hideSidebar = get(acf, "hide_sidebar", false);

    const video = get(resource, "acf.video", null);

    const { title, content } = resource;

    const aspect = getImageAspect(image, 16 / 9);

    const formID = get(acf, "wall_hubspot_form_id");

    const nextPost = get(resource, "next");

    const prevPost = get(resource, "previous");

    const slug = get(resource, "slug");

    const link = get(resource, "link");

    const contentWithIds = addHeadingIds(content.rendered);

    const modulesOnly =
        modules &&
        modules.length &&
        !["casestudies", "posts", "glossary", "podcasts"].includes(type);

    const dateISO = new Date(get(resource, "date")).toISOString();
    const dateString = new Date(dateISO);
    const dateOptions = { month: "long", day: "numeric", year: "numeric" };
    const dateText = new Intl.DateTimeFormat("en-US", dateOptions).format(dateString);
    const eyebrowText = readTime(resource);
    const breadCrumb = link.replace(slug,'');

    return (
        <>
            <SidebarColumn
                type={type} // Pass type to SidebarColumn
                contentWithIds={contentWithIds} // Pass processed content with IDs to SidebarColumn
                hasBlocks={modules && modules.length}
                hideSidebar={hideSidebar}
                className="sidebar-column"
            >
                {/* Image - Not visible for case studies */}

                {!modulesOnly && image && !video && !hideSidebar && (
                    <FeaturedImage>
                        <Visual
                            aspect={aspect}
                            background="cover"
                            image={image}
                            isLcpCandidate
                        />
                    </FeaturedImage>
                )}

                {!modulesOnly && video && (
                    <Video extend={featureVideoOrImageStyle} content={video} />
                )}

                {!modulesOnly && title && !hideSidebar && (
                    <Title
                        as="h1"
                        dangerouslySetInnerHTML={{ __html: title.rendered }}
                    />
                )}

                {/* Breadcrumbs for Videos */}
                {["videos"].includes(type) && (
                    <FelaComponent
                    as="div"
                    style={combineRules(textStyles.tag,revuCrumbStyle)}
                    >
                        <WordpressLink url={breadCrumb} style={breadcrumbsStyle}>
                          <svg xmlns="http://www.w3.org/2000/svg" fill="#3B58F5" width="18" height="18" shapeRendering="geometricPrecision" textRendering="geometricPrecision" imageRendering="optimizeQuality" fillRule="evenodd" clipRule="evenodd" viewBox="0 0 512 463.95"><path fillRule="nonzero" d="M512 332.66H268.5v92.31c-.68 15.47-5.77 26.46-15.43 32.82-25.79 17.2-52.31-5.26-69.24-22.6L14.33 261.6c-19.11-17.28-19.11-41.93 0-59.21L188.71 24.42c16.06-16.39 40.56-34.09 64.36-18.21 9.66 6.35 14.75 17.34 15.43 32.81v92.31H512v201.33z"/></svg>
                          <span>Back to Demo Library</span>
                        </WordpressLink>
                    </FelaComponent>
                )}

                {["posts"].includes(type) && (
                    <FelaComponent
                        style={combineRules(postTimeStyle,postMetaWrapStyle)}
                        as="div"
                    >
                        <FelaComponent
                        as="div"
                        style={combineRules(textStyles.tag,mainCrumbStyle)}
                        >
                            <WordpressLink url={breadCrumb}> Revenue Blog </WordpressLink>&nbsp;&gt; {title.rendered}
                        </FelaComponent>
                        {eyebrowText && (
                            <FelaComponent as="span" style={textStyles.tag}>
                                {eyebrowText}
                            </FelaComponent>
                        )}
                        {eyebrowText && (
                            <FelaComponent as="span" style={textStyles.tag}>
                                {dateText}
                            </FelaComponent>
                        )}
                    </FelaComponent>
                )}

                {["glossary"].includes(type) && (
                    <FelaComponent
                        style={combineRules(postTimeStyle,postMetaWrapStyle)}
                        as="div"
                    >
                        <FelaComponent
                        as="div"
                        style={combineRules(textStyles.tag,mainCrumbStyle)}
                        >
                            <WordpressLink url={breadCrumb}> Inside Sales Glossary </WordpressLink>&nbsp;&gt; {title.rendered}
                        </FelaComponent>
                        <FelaComponent
                        as="div"
                        style={combineRules(textStyles.tag,mainCrumbStyle)}
                        >
                        </FelaComponent>
                    </FelaComponent>
                )}

                {!modulesOnly && content && (
                    <PostContent content={contentWithIds} />
                )}

                {/* CATEGORIES */}
                <TaxonomyList
                    extend={categoryListStyle}
                    resource={resource}
                    slug="category"
                    dataKey="categories"
                    label="Categories"
                />

                {/* TAGS */}
                <TaxonomyList
                    extend={tagListStyle}
                    resource={resource}
                    slug="tag"
                    dataKey="tags"
                    label="Tags"
                />
                {["posts"].includes(type) && (
                    <FelaComponent as="div" style={combineRules(textStyles.paragraphSmall,breadcrumbsStyle,categoryListStyle)}>
                    { prevPost ? <WordpressLink url={prevPost.link}>&lt; Previous</WordpressLink> : ''}
                    { nextPost ? <WordpressLink url={nextPost.link}>Next &gt;</WordpressLink> : ''}
                    </FelaComponent>
                )}
                {/* Case Studies have an optional hubspot form as a content wall */}
                {["casestudies"].includes(type) && formID && (
                    <HubSpotForm
                        formId={formID}
                        prefix="resource-wall"
                        extend={{
                            marginV: 60
                        }}
                    />
                )}

                {["posts", "glossary", "podcasts"].includes(type) && (
                    <RelatedPosts resource={resource} />
                )}
            </SidebarColumn>

            {/* Support Modules for any resource type that can display them */}
            {modules && modules.length && <Blocks blocks={acf.modules} />}

        </>
    );
}

const mainCrumbStyle = {
  display: "flex",
  width: "100%",
  flexShrink: 0,
  marginBottom: 6,
  "> a": {
      color: "#3B58F5"
  }
};
const revuCrumbStyle = {
  display: "flex",
  width: "100%",
  flexShrink: 0,
  marginBottom: 6,
  paddingTop: 24,
  "> a span": {
      display: "flex",
      alignItems: "center",
      color: "#3B58F5"
  },
  "> a span span": {
      marginLeft: "6px",
      fontSize: "18px"
  }
};
const breadcrumbsStyle = {
  display: "flex !important",
  justifyContent: "space-between",
  "> a": {
    display: "flex",

  }
};

const tagListStyle = {
    marginTop: 12
};
const categoryListStyle = {
    marginTop: 30
};

const featureVideoOrImageStyle = {
    borderRadius: "6px",
    position: "relative",
    overflow: "hidden",
    "> .vv-visual": {
        width: "100%",
        borderRadius: "6px",
        overflow: "hidden",
        "& img": {
            width: "100%"
        }
    },
    extend: [
        fluid("margin-bottom", {
            max: 25,
            min: 10
        })
    ]
};

const titleStyle = {
    extend: [
        fluid("margin-bottom", {
            max: 30,
            min: 10
        })
    ]
};

const Title = createComponent(
    combineRules(textStyles.heading3, titleStyle),
    "h1",
    ["dangerouslySetInnerHTML"]
);

const FeaturedImage = createComponent(() => ({
    position: "relative",
    width: "100%",
    "& img": {
        position: "absolute",
        top: 0,
        width: "100%",
        display: "block",
        overflow: "hidden",
        borderRadius: 6
    },
    "& .vv-visual": { height: "100%", width: "100%" },

    extend: [fluid("margin-bottom", { max: 20, min: 10 })]
}));

const postTimeStyle = {
    display: "flex",
    justifyContent: "space-between"
};

const postMetaWrapStyle = {
    width: "100%",
    flexWrap: "wrap",
    minWidthTablet: { marginBottom: 15 }
};
